import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type {
  EdpFile,
  EdpFormField,
  EdpItem,
  EdpPackageDetail,
  EdpReviewer,
} from '../../api/employeeDocsTypes.js';
import { ReviewerPicker } from './ReviewerPicker.js';
import { Modal } from '../../components/Modal.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../../components/ui.js';
import {
  FileList,
  FilePicker,
  FormFieldGrid,
  type PickedFile,
  ItemStatusBadge,
  PackageStatusBadge,
  RejectionNote,
  type FormValues,
} from '../../employeeDocs/shared.js';
import { formatDate, formatDateTime, humanizeAction } from '../../lib/format.js';
import type { EdpFormIssue } from '../../../shared/formValidation.js';
import { EDP_NEXT_ACTION, EDP_REJECTION_REASONS } from '../../../shared/employeeDocs.js';

export function EmployeeDocsPackagePage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const queryKey = ['edp', 'package', id];
  const detail = useQuery({
    queryKey,
    queryFn: () => apiRequest<EdpPackageDetail>(`/api/employee-documents/packages/${id}`),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey: ['edp', 'packages'] });
  };

  const [error, setError] = useState<string | null>(null);
  const act = (path: string, body?: unknown) =>
    apiRequest(`/api/employee-documents/packages/${id}/${path}`, { method: 'POST', body })
      .then(() => setError(null))
      .then(refresh)
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiRequestError ? cause.message : 'The action could not be completed.',
        ),
      );

  const action = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => act(path, body),
  });
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [editReviewers, setEditReviewers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: () => apiRequest(`/api/employee-documents/packages/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['edp', 'packages'] });
      navigate('/employee-documents', { replace: true });
    },
    onError: (cause: unknown) => {
      setConfirmDelete(false);
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The package could not be deleted.',
      );
    },
  });

  if (detail.isLoading) return <Spinner label="Loading package" />;
  if (detail.isError || !detail.data) {
    return (
      <Alert variant="error" title="Package not found">
        <Link to="/employee-documents" className="underline">
          Back to employee documents
        </Link>
      </Alert>
    );
  }

  const {
    package: pkg,
    items,
    completeness,
    events,
    activeGrants,
    canDecide,
    canManage,
  } = detail.data;
  const uspsReturned = pkg.status === 'usps_returned';
  const reviewing = pkg.status === 'submitted' || uspsReturned;
  const withEmployee = pkg.status === 'corrections_requested';
  const heldByEmployee = ['invited', 'in_progress', 'corrections_requested'].includes(pkg.status);
  const canEditContents = canDecide && pkg.status !== 'cancelled';
  const allAccepted = items
    .filter((item) => item.isRequired)
    .every((item) => item.status === 'accepted');
  const anyRejected = items.some((item) => item.status === 'rejected');
  const undecided = items.filter((item) => item.isRequired && item.status === 'completed').length;
  const employeeGrant = activeGrants.find((grant) => grant.subjectKind === 'employee');
  const recipientGrant = activeGrants.find((grant) => grant.subjectKind === 'recipient');
  const busy = action.isPending;
  const hasFiles = items.some((item) => item.files.length > 0);

  return (
    <div>
      <PageHeader
        title={`${pkg.employee.fullName} — ${pkg.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <PackageStatusBadge status={pkg.status} reviewStarted={pkg.reviewStarted} />
            <span>Waiting on: {EDP_NEXT_ACTION[pkg.status]}</span>
          </span>
        }
        actions={
          <span className="flex flex-wrap items-center gap-3">
            {hasFiles && (
              <a
                href={`/api/employee-documents/packages/${id}/archive`}
                className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
              >
                Download all (ZIP)
              </a>
            )}
            <Link
              to="/employee-documents"
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              All packages
            </Link>
          </span>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          {reviewing && canDecide && (
            <Card title={uspsReturned ? 'Returned by USPS' : 'QA review'}>
              {uspsReturned ? (
                <p className="text-sm text-slate-700">
                  The Contracting Officer returned the documents marked below
                  {pkg.uspsReturnedAt ? ` on ${formatDateTime(pkg.uspsReturnedAt)}` : ''}; their
                  USPS link has been withdrawn. Fix small slips in place with{' '}
                  <strong>Edit answers</strong> or by uploading a corrected file, then{' '}
                  <strong>Accept</strong> each item. For anything the employee must redo, click{' '}
                  <strong>Return for corrections now</strong> to email them the USPS reasons. Once
                  every required item is accepted, approve and re-send: USPS receives a fresh link.
                </p>
              ) : (
                <p className="text-sm text-slate-700">
                  Open each document, then accept it or return it with a reason. Once every item has
                  a decision and any was returned, the package goes back to the employee
                  automatically with a correction email. The Contracting Officer is emailed only
                  when every required item is accepted and you click{' '}
                  <strong>Approve and send to USPS</strong>.
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  disabled={!allAccepted || busy}
                  onClick={() => setConfirmRelease(true)}
                  title={allAccepted ? undefined : `${undecided} item(s) still need a decision`}
                >
                  Approve and send to USPS
                </Button>
                <Button
                  variant="secondary"
                  disabled={!anyRejected || busy}
                  loading={busy}
                  onClick={() => void action.mutate({ path: 'request-corrections' })}
                  title={anyRejected ? undefined : 'Return at least one item first'}
                >
                  Return for corrections now
                </Button>
              </div>
            </Card>
          )}
          {reviewing && !canDecide && (
            <Alert variant="info">
              This package is assigned to{' '}
              {pkg.reviewers.map((reviewer) => reviewer.fullName).join(', ')} for QA review. You can
              view it but not decide.
            </Alert>
          )}
          {pkg.status === 'released' && canDecide && (
            <Card title="Released to USPS">
              {pkg.uspsAcceptedAt && (
                <Alert variant="success" className="mb-3" title="Accepted by USPS">
                  {pkg.uspsAcceptedBy ?? 'The Contracting Officer'} accepted this package on{' '}
                  {formatDateTime(pkg.uspsAcceptedAt)}.
                </Alert>
              )}
              <p className="text-sm text-slate-700">
                Found a problem after release? Small slips on a form can be fixed with{' '}
                <strong>Edit answers</strong>. For anything the employee must redo, return the
                items: the USPS link is withdrawn, the employee is emailed the reasons, and the
                package comes back to QA for re-approval and re-release once corrected.
              </p>
              <div className="mt-4">
                <Button variant="danger" disabled={busy} onClick={() => setRecalling(true)}>
                  Return items to employee
                </Button>
              </div>
            </Card>
          )}
          {withEmployee && canDecide && anyRejected && (
            <Alert variant="info">
              Changed your mind about a returned item? You can still accept it here. Once no
              returned items remain, QA review resumes without the employee resubmitting.
            </Alert>
          )}
          {heldByEmployee && canDecide && completeness.complete && (
            <Card title="Ready for QA">
              <p className="text-sm text-slate-700">
                Every required item is complete but the employee has not pressed{' '}
                <strong>Submit for review</strong>. You can submit it for them; the history records
                that it was submitted on the employee&apos;s behalf.
              </p>
              <div className="mt-4">
                <Button
                  disabled={busy}
                  loading={busy}
                  onClick={() => void action.mutate({ path: 'submit-on-behalf' })}
                >
                  Submit for QA on the employee&apos;s behalf
                </Button>
              </div>
            </Card>
          )}
          {!reviewing &&
            !completeness.complete &&
            pkg.status !== 'released' &&
            pkg.status !== 'cancelled' && (
              <Card title="Still outstanding for the employee">
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-800">
                  {completeness.outstanding.map((entry) => (
                    <li key={entry.itemId}>
                      <span className="font-medium">{entry.name}</span> — {entry.reason}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

          {items.map((item) => (
            <ReviewItemCard
              key={item.id}
              packageId={id}
              item={item}
              canDecide={reviewing && canDecide}
              canAcceptReturned={withEmployee && canDecide}
              canEdit={canEditContents}
              onChanged={refresh}
              onError={setError}
            />
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <Card title="Package">
            <DefinitionList
              columns={1}
              items={[
                { label: 'Employee', value: `${pkg.employee.fullName} (${pkg.employee.email})` },
                {
                  label: pkg.reviewers.length === 1 ? 'QA reviewer' : 'QA reviewers',
                  value: pkg.reviewers.map((r) => `${r.fullName} (${r.email})`).join('; '),
                },
                {
                  label: 'Contracting Officer',
                  value: `${pkg.recipient.name} (${pkg.recipient.email})`,
                },
                { label: 'Due', value: pkg.dueAt ? formatDate(pkg.dueAt) : '—' },
                { label: 'Invited', value: formatDateTime(pkg.invitedAt) },
                { label: 'Submitted', value: formatDateTime(pkg.submittedAt) },
                { label: 'Approved', value: formatDateTime(pkg.approvedAt) },
                { label: 'Released to USPS', value: formatDateTime(pkg.releasedAt) },
                ...(pkg.uspsReturnedAt
                  ? [{ label: 'Returned by USPS', value: formatDateTime(pkg.uspsReturnedAt) }]
                  : []),
                ...(pkg.uspsAcceptedAt
                  ? [
                      {
                        label: 'Accepted by USPS',
                        value: `${formatDateTime(pkg.uspsAcceptedAt)}${pkg.uspsAcceptedBy ? ` (${pkg.uspsAcceptedBy})` : ''}`,
                      },
                    ]
                  : []),
                {
                  label: 'Created by',
                  value: `${pkg.createdByName}, ${formatDateTime(pkg.createdAt)}`,
                },
              ]}
            />
          </Card>

          <Card title="Access links">
            <dl className="text-sm">
              <dt className="font-medium text-slate-900">Employee</dt>
              <dd className="mb-2 text-slate-700">
                {employeeGrant
                  ? `Active until ${formatDate(employeeGrant.expiresAt)}${employeeGrant.firstUsedAt ? ', opened' : ', not yet opened'}`
                  : 'No active link'}
              </dd>
              <dt className="font-medium text-slate-900">Contracting Officer</dt>
              <dd className="text-slate-700">
                {recipientGrant
                  ? `Active until ${formatDate(recipientGrant.expiresAt)}${recipientGrant.firstUsedAt ? ', opened' : ', not yet opened'}`
                  : 'No active link'}
              </dd>
            </dl>
            {canManage && (
              <div className="mt-3 flex flex-wrap gap-2">
                {pkg.status !== 'cancelled' && (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => setEditReviewers(true)}
                  >
                    Change reviewers
                  </Button>
                )}
                {pkg.status !== 'released' && pkg.status !== 'cancelled' && (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void action.mutate({ path: 'resend-invitation' })}
                  >
                    Re-send employee invitation
                  </Button>
                )}
                {employeeGrant && (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void action.mutate({
                        path: 'revoke-access',
                        body: { subjectKind: 'employee' },
                      })
                    }
                  >
                    Revoke employee link
                  </Button>
                )}
                {pkg.status === 'released' && (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void action.mutate({ path: 'resend-release' })}
                  >
                    Re-send to Contracting Officer
                  </Button>
                )}
                {recipientGrant && (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void action.mutate({
                        path: 'revoke-access',
                        body: { subjectKind: 'recipient' },
                      })
                    }
                  >
                    Revoke USPS link
                  </Button>
                )}
                {pkg.status !== 'released' && pkg.status !== 'cancelled' && (
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm('Cancel this package? The employee link stops working.')) {
                        void action.mutate({ path: 'cancel' });
                      }
                    }}
                  >
                    Cancel package
                  </Button>
                )}
                <Button
                  variant="danger"
                  disabled={busy || remove.isPending}
                  onClick={() => {
                    setDeleteTyped('');
                    setConfirmDelete(true);
                  }}
                >
                  Delete permanently
                </Button>
              </div>
            )}
          </Card>

          <Card title="History">
            <ol className="space-y-2 text-sm">
              {events.map((event) => (
                <li key={event.id} className="border-l-2 border-slate-200 pl-3">
                  <p className="text-slate-900">
                    {humanizeAction(event.action)}
                    {event.itemName && <span className="text-slate-600"> — {event.itemName}</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatDateTime(event.occurredAt)} ·{' '}
                    {event.actorName ?? event.actorEmail ?? event.actorKind}
                  </p>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>

      {recalling && (
        <RecallModal
          packageId={id}
          items={items}
          onClose={() => setRecalling(false)}
          onDone={async () => {
            setRecalling(false);
            setError(null);
            await refresh();
          }}
        />
      )}

      <Modal
        open={confirmRelease}
        onClose={() => setConfirmRelease(false)}
        title="Approve and send to USPS"
        description={`${pkg.recipient.name} (${pkg.recipient.email}) will be emailed a secure link to download the approved package. No documents are attached to the email. The employee's link will stop working.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRelease(false)}>
              Back
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                setConfirmRelease(false);
                void action.mutate({ path: 'approve-and-send' });
              }}
            >
              Approve and send
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          By approving you confirm that you have opened and checked every document in this package.
        </p>
      </Modal>

      {editReviewers && (
        <ReviewersModal
          packageId={id}
          current={pkg.reviewers.map((reviewer) => reviewer.id)}
          onClose={() => setEditReviewers(false)}
          onSaved={() => {
            setEditReviewers(false);
            void refresh();
          }}
        />
      )}

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this package permanently"
        description={`Every form, uploaded file, signature, link and history entry for ${pkg.employee.fullName} in this package is erased and cannot be recovered. The employee and USPS links stop working immediately. If ${pkg.employee.fullName} has no other package, the employee record is deleted too.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Back
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              disabled={deleteTyped.trim() !== pkg.employee.fullName}
              onClick={() => remove.mutate()}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <Field label={`Type the employee's name (${pkg.employee.fullName}) to confirm`}>
          <TextInput
            value={deleteTyped}
            onChange={(event) => setDeleteTyped(event.target.value)}
            autoComplete="off"
          />
        </Field>
      </Modal>
    </div>
  );
}

function ReviewersModal({
  packageId,
  current,
  onClose,
  onSaved,
}: {
  packageId: string;
  current: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const reviewers = useQuery({
    queryKey: ['edp', 'reviewers'],
    queryFn: () => apiRequest<EdpReviewer[]>('/api/employee-documents/reviewers'),
  });
  const [selected, setSelected] = useState<string[]>(current);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/employee-documents/packages/${packageId}/reviewers`, {
        method: 'PUT',
        body: { reviewerUserIds: selected },
      }),
    onSuccess: onSaved,
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The reviewers could not be changed.',
      ),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="QA reviewers"
      description="Any one of the reviewers can accept or reject items and release the package; all of them are emailed when the employee submits."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={selected.length === 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <Alert variant="error">{error}</Alert>}
        {reviewers.isLoading ? (
          <Spinner label="Loading reviewers" />
        ) : (
          <ReviewerPicker
            idPrefix="p-reviewer"
            reviewers={reviewers.data ?? []}
            selected={selected}
            onChange={setSelected}
          />
        )}
      </div>
    </Modal>
  );
}

function ReviewItemCard({
  packageId,
  item,
  canDecide,
  canAcceptReturned,
  canEdit,
  onChanged,
  onError,
}: {
  packageId: string;
  item: EdpItem;
  canDecide: boolean;
  canAcceptReturned: boolean;
  canEdit: boolean;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>(EDP_REJECTION_REASONS[0].code);
  const [comment, setComment] = useState('');

  const decide = useMutation({
    mutationFn: (
      body: { decision: 'accept' } | { decision: 'reject'; reasonCode: string; comment?: string },
    ) =>
      apiRequest(`/api/employee-documents/packages/${packageId}/items/${item.id}/decision`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      onError(null);
      setRejecting(false);
      setComment('');
      await onChanged();
    },
    onError: (cause) =>
      onError(
        cause instanceof ApiRequestError ? cause.message : 'The decision could not be saved.',
      ),
  });

  const upload = useMutation({
    mutationFn: (file: PickedFile) => {
      const atLimit =
        item.kind === 'credential' &&
        item.files.filter((f) => !f.isRenderedForm).length >= item.maxFiles;
      const replaceFileId = atLimit ? item.files.find((f) => !f.isRenderedForm)?.id : undefined;
      return apiRequest(`/api/employee-documents/packages/${packageId}/items/${item.id}/files`, {
        method: 'POST',
        body: { ...file, replaceFileId },
      });
    },
    onSuccess: async () => {
      onError(null);
      await onChanged();
    },
  });
  const removeFile = useMutation({
    mutationFn: (fileId: string) =>
      apiRequest(`/api/employee-documents/packages/${packageId}/items/${item.id}/files/${fileId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      onError(null);
      await onChanged();
    },
    onError: (cause) =>
      onError(cause instanceof ApiRequestError ? cause.message : 'The file could not be removed.'),
  });

  const acceptOnly = canAcceptReturned && item.status === 'rejected';
  const decidable =
    acceptOnly ||
    (canDecide &&
      (item.status === 'completed' || item.status === 'accepted' || item.status === 'rejected'));
  const values = item.formValues ?? {};
  const fields = item.formFields ?? [];
  const editable =
    canEdit &&
    item.kind === 'form' &&
    item.signatureMode !== 'ink' &&
    fields.length > 0 &&
    item.formValues !== undefined &&
    item.status !== 'pending';
  const inkForm = item.kind === 'form' && item.signatureMode === 'ink';
  const canReplaceFiles =
    canEdit &&
    (item.kind === 'credential' || (inkForm && item.files.some((file) => file.isRenderedForm)));
  const removable = (file: EdpFile) => canEdit && !file.isRenderedForm;

  return (
    <Card
      title={
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{item.name}</h2>
          <ItemStatusBadge status={item.status} />
          {!item.isRequired && <span className="text-xs text-slate-500">optional</span>}
        </div>
      }
      actions={
        decidable || editable ? (
          <>
            {editable && (
              <Button
                variant="secondary"
                disabled={decide.isPending}
                onClick={() => setEditing(true)}
              >
                Edit answers
              </Button>
            )}
            {decidable && item.status !== 'accepted' && (
              <Button
                loading={decide.isPending}
                onClick={() => decide.mutate({ decision: 'accept' })}
              >
                Accept
              </Button>
            )}
            {decidable && item.status !== 'rejected' && !acceptOnly && (
              <Button
                variant="danger"
                disabled={decide.isPending}
                onClick={() => setRejecting(true)}
              >
                Return
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        <RejectionNote item={item} />
        {item.kind === 'form' && item.formIssues && <FormAudit item={item} fields={fields} />}
        {item.kind === 'form' && fields.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium text-slate-800">
              Entered values ({fields.length} fields)
            </summary>
            <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2">
              {fields.map((field) => {
                const value = values[field.name];
                return (
                  <div
                    key={field.name}
                    className="flex justify-between gap-2 border-b border-slate-100 py-1"
                  >
                    <dt className="text-slate-600">
                      {field.section ? `${field.section} – ${field.label}` : field.label}
                    </dt>
                    <dd className="text-right text-slate-900">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || '—'}
                    </dd>
                  </div>
                );
              })}
            </dl>
            {item.signedAt && (
              <p className="mt-2 text-slate-700">
                Signed by {item.signedName} on {formatDateTime(item.signedAt)}.
              </p>
            )}
          </details>
        )}
        <FileList
          files={item.files}
          hrefFor={(file) =>
            `/api/employee-documents/packages/${packageId}/files/${file.id}?inline=1`
          }
          onRemove={
            canEdit && item.files.some(removable)
              ? (file) => {
                  if (!removable(file)) return;
                  if (window.confirm(`Remove ${file.fileName} on the employee's behalf?`)) {
                    removeFile.mutate(file.id);
                  }
                }
              : undefined
          }
          removing={removeFile.isPending}
        />
        {canReplaceFiles && (
          <div className="rounded-md border border-dashed border-slate-300 p-3">
            <p className="mb-2 text-xs text-slate-600">
              {inkForm
                ? 'Upload a corrected signed scan on the employee\u2019s behalf; it replaces the current signed copy and is logged.'
                : `Upload a corrected file on the employee\u2019s behalf${item.files.length >= item.maxFiles ? ' (replaces the oldest file)' : ''}; the upload is logged with your name.`}
            </p>
            <FilePicker
              label={inkForm ? 'Upload signed scan (PDF)' : 'Upload file (PDF)'}
              disabled={upload.isPending || removeFile.isPending}
              onPick={(file) => upload.mutateAsync(file).then(() => undefined)}
            />
          </div>
        )}
      </div>

      {editing && (
        <EditAnswersModal
          packageId={packageId}
          item={item}
          fields={fields}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            onError(null);
            await onChanged();
          }}
        />
      )}

      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={`Return "${item.name}" for correction`}
        description="The employee is emailed the reason when you click Return for corrections on the package."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={decide.isPending}
              onClick={() =>
                decide.mutate({
                  decision: 'reject',
                  reasonCode,
                  comment: comment.trim() || undefined,
                })
              }
            >
              Return item
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Reason" htmlFor={`reason-${item.id}`} required>
            <Select
              id={`reason-${item.id}`}
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
            >
              {EDP_REJECTION_REASONS.map((reason) => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Note to the employee" htmlFor={`comment-${item.id}`}>
            <TextArea
              id={`comment-${item.id}`}
              rows={3}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </Field>
        </div>
      </Modal>
    </Card>
  );
}

/**
 * Returns chosen items of a released package to the employee, each with its own
 * reason and note; one correction email goes out for the lot.
 */
function RecallModal({
  packageId,
  items,
  onClose,
  onDone,
}: {
  packageId: string;
  items: readonly EdpItem[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const candidates = items.filter((item) => item.status !== 'pending');
  const [selected, setSelected] = useState<
    Record<string, { reasonCode: string; comment: string } | undefined>
  >({});
  const [error, setError] = useState<string | null>(null);
  const chosen = candidates.filter((item) => selected[item.id]);
  const recall = useMutation({
    mutationFn: () =>
      apiRequest(`/api/employee-documents/packages/${packageId}/recall`, {
        method: 'POST',
        body: {
          items: chosen.map((item) => {
            const entry = selected[item.id] as { reasonCode: string; comment: string };
            return {
              itemId: item.id,
              reasonCode: entry.reasonCode,
              comment: entry.comment.trim() || undefined,
            };
          }),
        },
      }),
    onSuccess: onDone,
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The items could not be returned.',
      ),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Return items to the employee"
      description="Tick each item to return and give the reason. The Contracting Officer's link is withdrawn and the employee receives one correction email listing every item."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={chosen.length === 0}
            loading={recall.isPending}
            onClick={() => recall.mutate()}
          >
            Return {chosen.length || ''} item{chosen.length === 1 ? '' : 's'} and email employee
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <ul className="flex flex-col gap-3">
        {candidates.map((item) => {
          const entry = selected[item.id];
          return (
            <li key={item.id} className="rounded-md border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <input
                  type="checkbox"
                  checked={Boolean(entry)}
                  onChange={(event) =>
                    setSelected((current) => ({
                      ...current,
                      [item.id]: event.target.checked
                        ? { reasonCode: EDP_REJECTION_REASONS[0].code, comment: '' }
                        : undefined,
                    }))
                  }
                />
                {item.name}
              </label>
              {entry && (
                <div className="mt-3 flex flex-col gap-3">
                  <Field label="Reason" htmlFor={`recall-reason-${item.id}`} required>
                    <Select
                      id={`recall-reason-${item.id}`}
                      value={entry.reasonCode}
                      onChange={(event) =>
                        setSelected((current) => ({
                          ...current,
                          [item.id]: { ...entry, reasonCode: event.target.value },
                        }))
                      }
                    >
                      {EDP_REJECTION_REASONS.map((reason) => (
                        <option key={reason.code} value={reason.code}>
                          {reason.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Note to the employee" htmlFor={`recall-comment-${item.id}`}>
                    <TextArea
                      id={`recall-comment-${item.id}`}
                      rows={2}
                      value={entry.comment}
                      onChange={(event) =>
                        setSelected((current) => ({
                          ...current,
                          [item.id]: { ...entry, comment: event.target.value },
                        }))
                      }
                    />
                  </Field>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

/**
 * Lets the reviewer correct a form's answers in place — the same boxes and red
 * messages the employee sees — for small slips that do not warrant sending the
 * whole form back. The completed PDF is regenerated; the employee's signature stands.
 */
function EditAnswersModal({
  packageId,
  item,
  fields,
  onClose,
  onSaved,
}: {
  packageId: string;
  item: EdpItem;
  fields: readonly EdpFormField[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [values, setValues] = useState<FormValues>(() => ({ ...(item.formValues ?? {}) }));
  const [issues, setIssues] = useState<readonly EdpFormIssue[]>(item.formIssues ?? []);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<{ changed: string[]; issues: EdpFormIssue[] }>(
        `/api/employee-documents/packages/${packageId}/items/${item.id}/form-values`,
        { method: 'PUT', body: { values } },
      ),
    onSuccess: async (result) => {
      setIssues(result.issues);
      setError(null);
      await onSaved();
    },
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The answers could not be saved.',
      ),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit answers — ${item.name}`}
      description={`Correct the employee's entries directly. The completed form is regenerated with the corrected answers${item.signedName ? ` and keeps the signature of ${item.signedName}` : ''}; the change is recorded in the package history.`}
      size="full"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save corrections
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <Alert variant="error">{error}</Alert>}
        {issues.length > 0 && (
          <p className="text-sm text-red-800">
            {issues.length} problem{issues.length === 1 ? '' : 's'} remain against the form's rules;
            they are marked in red below.
          </p>
        )}
        <FormFieldGrid
          fields={fields}
          values={values}
          issues={issues}
          onChange={(name, value) => setValues((current) => ({ ...current, [name]: value }))}
        />
      </div>
    </Modal>
  );
}

/**
 * The automatic audit of a form's saved answers: the same checks that gate the
 * employee's submission, so the reviewer sees at a glance whether the form passed
 * (or, on an older submission, exactly which entries fall short of the form's rules).
 */
function FormAudit({ item, fields }: { item: EdpItem; fields: readonly EdpFormField[] }) {
  const issues = item.formIssues ?? [];
  const byName = new Map(fields.map((field) => [field.name, field]));
  if (issues.length === 0) {
    return (
      <p className="text-sm text-emerald-800">
        Automatic audit: passed — every required block is filled in and every entry matches the
        form's format and history rules.
      </p>
    );
  }
  return (
    <details className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm" open>
      <summary className="cursor-pointer font-medium text-red-800">
        Automatic audit: {issues.length} problem{issues.length === 1 ? '' : 's'} found
      </summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-red-900">
        {issues.map((issue, index) => {
          const field = issue.field ? byName.get(issue.field) : undefined;
          const where = field
            ? field.section
              ? `${field.section} – ${field.label}`
              : field.label
            : issue.section;
          return (
            <li key={index}>
              {where && <span className="font-medium">{where}: </span>}
              {issue.message}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
